import React, { useEffect, useState, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { getCredits, getAlerts } from "@/lib/api";
import { LogOut, User as UserIcon, Zap, Menu, X, Bell } from "lucide-react";

export default function Header() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [credits, setCredits] = useState(null);
  const [unreadAlerts, setUnreadAlerts] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);

  const refreshCredits = useCallback(async () => {
    if (!user) { setCredits(null); setUnreadAlerts(0); return; }
    try {
      const c = await getCredits();
      setCredits(c);
    } catch {
      setCredits(null);
    }
    try {
      const a = await getAlerts(1);
      setUnreadAlerts(a.unread_count || 0);
    } catch { /* silent */ }
  }, [user]);

  useEffect(() => { refreshCredits(); }, [refreshCredits, location.pathname]);

  // Refresh unread badge instantly when alerts are marked read elsewhere (e.g. Watchlist page)
  useEffect(() => {
    const onAlertsChanged = () => {
      if (!user) return;
      getAlerts(1).then((a) => setUnreadAlerts(a.unread_count || 0)).catch(() => {});
    };
    window.addEventListener("alerts-changed", onAlertsChanged);
    return () => window.removeEventListener("alerts-changed", onAlertsChanged);
  }, [user]);

  // Close the mobile menu whenever the route changes
  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleLogin = () => {
    const redirectUrl = window.location.origin + "/my-reports";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const hasSub = credits?.subscription_status === "active";
  const navLinkClass = "font-mono-plex text-xs uppercase tracking-widest hover:text-[#FF331F]";
  const mobileLinkClass = "block font-mono-plex text-xs uppercase tracking-widest px-6 py-4 hover:bg-[#111] hover:text-[#F4F4F0] transition-colors";

  return (
    <header
      className="sticky top-0 z-40 border-b-2 border-[#111111] bg-[#F4F4F0]/95 backdrop-blur-md"
      data-testid="site-header"
    >
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 md:px-10 h-16 flex items-center justify-between gap-3">
        <Link to="/" className="flex items-baseline gap-2 min-w-0" data-testid="logo-link">
          <span className="font-serif-display text-2xl sm:text-3xl font-black tracking-tighter leading-none">
            MonetizeGPS
          </span>
          <span className="hidden sm:inline font-mono-plex text-[10px] uppercase tracking-[0.25em] text-[#555] border border-[#111] px-1.5 py-[2px]">
            VOL.01
          </span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-4">
          <Link to="/" className={navLinkClass} data-testid="nav-home">
            Analyze
          </Link>
          <Link to="/pricing" className={navLinkClass} data-testid="nav-pricing">
            Pricing
          </Link>
          <Link to="/showcase" className={navLinkClass} data-testid="nav-showcase">
            Showcase
          </Link>
          {user ? (
            <>
              <Link
                to="/my-reports"
                className={`${navLinkClass} whitespace-nowrap`}
                data-testid="nav-my-reports"
              >
                My Blueprints
              </Link>
              <Link
                to="/watchlist"
                className={`${navLinkClass} whitespace-nowrap relative inline-flex items-center gap-1`}
                data-testid="nav-watchlist"
              >
                <Bell size={13} />
                Watchlist
                {unreadAlerts > 0 && (
                  <span className="bg-[#FF331F] text-white font-mono-plex text-[9px] px-1 py-[1px] leading-none" data-testid="alerts-badge">
                    {unreadAlerts}
                  </span>
                )}
              </Link>
              {credits !== null && (
                <Link
                  to={hasSub || credits.credits > 0 ? "/my-reports" : "/pricing"}
                  className={`inline-flex items-center gap-1.5 font-mono-plex text-[10px] uppercase tracking-widest px-2 py-1 border-2 transition-colors ${
                    hasSub
                      ? "border-[#FF331F] bg-[#FF331F] text-white hover:opacity-90"
                      : credits.total > 0
                        ? "border-[#111] bg-white text-[#111] hover:bg-[#111] hover:text-[#F4F4F0]"
                        : "border-[#111] bg-[#111] text-[#F4F4F0] hover:bg-[#FF331F] hover:border-[#FF331F]"
                  }`}
                  data-testid="credits-badge"
                  title={
                    hasSub
                      ? `Monthly: ${credits.monthly_credits} · Pack: ${credits.credits}${credits.subscription_expires_at ? ` · Renews ${new Date(credits.subscription_expires_at).toLocaleDateString()}` : ""}`
                      : `${credits.credits} lookups`
                  }
                >
                  <Zap size={11} />
                  {hasSub ? `${credits.total} LOOKUPS` : `${credits.credits} LOOKUPS`}
                </Link>
              )}
              <div className="flex items-center gap-2 ml-2">
                <Link to="/account" title="Account" data-testid="nav-account" className="shrink-0">
                  {user.picture ? (
                    <img
                      src={user.picture}
                      alt={user.name}
                      className="w-8 h-8 border border-[#111] object-cover hover:ring-2 hover:ring-[#FF331F]"
                    />
                  ) : (
                    <div className="w-8 h-8 border border-[#111] bg-white flex items-center justify-center hover:ring-2 hover:ring-[#FF331F]">
                      <UserIcon size={16} />
                    </div>
                  )}
                </Link>
                <button
                  onClick={logout}
                  className={`${navLinkClass} flex items-center gap-1`}
                  data-testid="logout-button"
                >
                  <LogOut size={14} /> <span>Logout</span>
                </button>
              </div>
            </>
          ) : (
            <button
              onClick={handleLogin}
              className="font-mono-plex text-xs uppercase tracking-widest whitespace-nowrap bg-[#111111] text-[#F4F4F0] px-4 py-2 border border-[#111111] hover:bg-[#FF331F] transition-colors"
              data-testid="login-button"
              title="You'll be redirected to our secure sign-in provider"
            >
              Sign in
            </button>
          )}
        </nav>

        {/* Mobile controls */}
        <div className="flex md:hidden items-center gap-2 shrink-0">
          {!user && (
            <button
              onClick={handleLogin}
              className="font-mono-plex text-[11px] uppercase tracking-widest whitespace-nowrap bg-[#111111] text-[#F4F4F0] px-3 py-2 border border-[#111111] hover:bg-[#FF331F] transition-colors"
              data-testid="login-button-mobile"
            >
              Sign in
            </button>
          )}
          {user && credits !== null && (
            <Link
              to="/watchlist"
              className="relative inline-flex items-center justify-center w-9 h-9 border-2 border-[#111] bg-white"
              data-testid="mobile-alerts-bell"
              aria-label="Watchlist alerts"
            >
              <Bell size={14} />
              {unreadAlerts > 0 && (
                <span className="absolute -top-1.5 -right-1.5 bg-[#FF331F] text-white font-mono-plex text-[9px] px-1 py-[1px] leading-none" data-testid="alerts-badge-mobile">
                  {unreadAlerts}
                </span>
              )}
            </Link>
          )}
          {user && credits !== null && (
            <Link
              to={hasSub || credits.credits > 0 ? "/my-reports" : "/pricing"}
              className={`inline-flex items-center gap-1 font-mono-plex text-[10px] uppercase tracking-widest px-2 py-1 border-2 whitespace-nowrap ${
                hasSub
                  ? "border-[#FF331F] bg-[#FF331F] text-white"
                  : credits.total > 0
                    ? "border-[#111] bg-white text-[#111]"
                    : "border-[#111] bg-[#111] text-[#F4F4F0]"
              }`}
              data-testid="credits-badge-mobile"
            >
              <Zap size={10} />
              {credits.total}
            </Link>
          )}
          <button
            onClick={() => setMenuOpen((v) => !v)}
            className="w-10 h-10 flex items-center justify-center border-2 border-[#111] bg-white hover:bg-[#111] hover:text-[#F4F4F0] transition-colors"
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            aria-expanded={menuOpen}
            data-testid="mobile-menu-button"
          >
            {menuOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>

        {/* Mobile menu panel */}
        {menuOpen && (
          <div
            className="md:hidden absolute top-full left-0 right-0 h-screen bg-[#111]/20"
            onClick={() => setMenuOpen(false)}
            data-testid="mobile-menu-backdrop"
          />
        )}
        {menuOpen && (
          <nav
            className="md:hidden absolute top-full inset-x-0 bg-[#F4F4F0] border-b-2 border-[#111] divide-y divide-[#ddd] shadow-[0_8px_0_rgba(17,17,17,0.08)]"
            data-testid="mobile-menu"
          >
            <Link to="/" className={mobileLinkClass} onClick={() => setMenuOpen(false)} data-testid="mobile-nav-home">
              Analyze
            </Link>
            <Link to="/pricing" className={mobileLinkClass} onClick={() => setMenuOpen(false)} data-testid="mobile-nav-pricing">
              Pricing
            </Link>
            <Link to="/showcase" className={mobileLinkClass} onClick={() => setMenuOpen(false)} data-testid="mobile-nav-showcase">
              Showcase
            </Link>
            {user && (
              <>
                <Link to="/my-reports" className={mobileLinkClass} onClick={() => setMenuOpen(false)} data-testid="mobile-nav-my-reports">
                  My Blueprints
                </Link>
                <Link to="/watchlist" className={`${mobileLinkClass} flex items-center gap-2`} onClick={() => setMenuOpen(false)} data-testid="mobile-nav-watchlist">
                  Watchlist
                  {unreadAlerts > 0 && (
                    <span className="bg-[#FF331F] text-white font-mono-plex text-[9px] px-1 py-[1px] leading-none">{unreadAlerts}</span>
                  )}
                </Link>
                <Link to="/account" className={mobileLinkClass} onClick={() => setMenuOpen(false)} data-testid="mobile-nav-account">
                  Account
                </Link>
                <button
                  onClick={() => { setMenuOpen(false); logout(); }}
                  className={`${mobileLinkClass} w-full text-left flex items-center gap-2`}
                  data-testid="mobile-logout-button"
                >
                  <LogOut size={14} /> Logout
                </button>
              </>
            )}
          </nav>
        )}
      </div>
    </header>
  );
}
